import React, { useCallback, useContext } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useParams } from "react-router";
import { useQuery } from "@apollo/client";
import {
    GetNewsSelectionFilesManager,
    GetNewsSelectionFilesManagerVariables
} from "src/api/generated/GetNewsSelectionFilesManager";
import * as Screens from "src/screens";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { GET_NEWS_SELECTION_FILES_MANAGER, GET_NEWS_SELECTION_FILES_MANAGER_IT2 } from "src/api/news";
import { AddNewsDocumentsModalList } from "./list/AddNewsDocumentsModalList";
import { getDate } from "src/utils/Date";
import styles from "./AddNewsDocumentsModal.module.css";
import { RootStoreContext } from "src/stores/RootStore";
import { ErpType } from "src/network/User";

interface Params {
    realestateid: string;
}

export interface NewsAddDocumentsListFile {
    fileId?: string;
    fileName?: string;
    fileDate?: string;
    fileType?: number;
}

const AddNewsDocumentsModalBase = () => {
    const { t } = useTranslation();
    const { realestateid } = useParams<Params>();
    const { authStore, navStore, uiStore } = useContext(RootStoreContext);

    const variablesRequest =
        authStore.user?.erpType === ErpType.IT2 && navStore.consolidationId
            ? {
                  realestateid: realestateid,
                  consolidationid: navStore.consolidationId
              }
            : {
                  realestateid: realestateid
              };

    const { data, loading, error } = useQuery<GetNewsSelectionFilesManager, GetNewsSelectionFilesManagerVariables>(
        authStore.user?.erpType === ErpType.IT2 && navStore.consolidationId
            ? GET_NEWS_SELECTION_FILES_MANAGER_IT2
            : GET_NEWS_SELECTION_FILES_MANAGER,
        { variables: variablesRequest }
    );

    const onCloseAddDocumentsModal = useCallback(() => {
        uiStore.setIsAddNewsDocumentClicked(false);
    }, [uiStore]);

    if (loading) {
        return <Screens.Loading />;
    }
    if (error) {
        return <Screens.Error message={error.message} networkError={error.networkError} />;
    }
    if (!data) {
        return <Screens.Error message={t("error.nodata").toString()} />;
    }

    const fileItems: NewsAddDocumentsListFile[] = [];

    data.accountlistfiles.forEach((file) => {
        const fileToAdd: NewsAddDocumentsListFile = {
            fileId: file.fileid,
            fileName: file.name ?? "",
            fileDate: getDate(file.filedate),
            fileType: file.type ?? 0
        };

        fileItems.push(fileToAdd);
    });

    data.realestatefiles.forEach((file) => {
        const fileToAdd: NewsAddDocumentsListFile = {
            fileId: file.fileid,
            fileName: file.name ?? "",
            fileDate: getDate(file.filedate),
            fileType: file.type ?? 0
        };

        fileItems.push(fileToAdd);
    });

    return (
        <UI.Modal
            show={uiStore.isAddNewsDocumentClicked}
            onClose={onCloseAddDocumentsModal}
            isScrollable={true}
            className={styles.AddDocumentsModal}
            size="lg"
        >
            <AddNewsDocumentsModalList files={fileItems} />
            <div className="text-center">
                <UI.Button
                    className={`${styles.AddDocumentsModalButton} mt-3`}
                    label={t("labels.cancel")}
                    onClick={onCloseAddDocumentsModal}
                    variant="link"
                />
            </div>
        </UI.Modal>
    );
};

export const AddNewsDocumentsModal = Sentry.withProfiler(observer(AddNewsDocumentsModalBase));
